import { ɵisPromise as isPromise } from '@angular/core';
import { Observable, from, isObservable, of, ReplaySubject, share } from 'rxjs';

export function isDefined<T>(
  arg: T | null | undefined,
): arg is T extends null | undefined ? never : T {
  return arg !== null && arg !== undefined;
}

export function clamp(
  value: number,
  min: number = 0,
  max: number = 100,
): number {
  return Math.max(min, Math.min(max, value));
}

/**
 * Convert any given value to an array, if it isn't already one.
 */
export function transformToArray<T>(value: T | T[]): T[] {
  return Array.isArray(value) ? value : [value];
}

export function wrapIntoObservable<T>(
  value: T | Promise<T> | Observable<T>,
): Observable<T> {
  if (isObservable(value)) {
    return value;
  }

  if (isPromise(value)) {
    // Use `Promise.resolve()` to wrap promise-like instances.
    // Required ie when a Resolver returns a AngularJS `$q` promise to correctly trigger the
    // change detection.
    return from(Promise.resolve(value));
  }

  return of(value);
}

export function flatten<T>(arr: T[][]): T[] {
  return ([] as T[]).concat(...arr);
}

// Replacement of publishReplay(1) + refCount()
export function publishRef<T>(source$: Observable<T>): Observable<T> {
  const replaySubject: ReplaySubject<T> = new ReplaySubject<T>(1);
  return source$.pipe(
    share({
      connector: () => replaySubject,
      resetOnError: false,
      resetOnComplete: false,
      resetOnRefCountZero: true,
    }),
  );
}

export function insertInArrayByIndex<T>(arr: T[], index: number, item: T): T[] {
  const arrCopy: T[] = [...arr];
  arrCopy.splice(index, 0, item);
  return arrCopy;
}

export function deleteFromArrayByIndex<T>(arr: T[], index: number): T[] {
  const arrCopy: T[] = [...arr];
  arrCopy.splice(index, 1);
  return arrCopy;
}
